<template>
  <div class="certification-apply-page">
    <Navbar />
    <PageBanner :title="t('navbar.certificationApply')" />

    <!-- Breadcrumb Area -->
    <Breadcrumb :items="breadcrumbItems" />

    <div class="content">
      <div class="cards-container">
        <!-- 左側：完訓名單提供 -->
        <div class="card card-left">
          <div class="card-content">
            <h2 class="card-title">完訓名單提供</h2>
            <div class="card-info">
              <p class="target-group">
                適用對象：<span class="highlight-blue"
                  >合作學協會授權承辦人</span
                >
              </p>
              <p class="purpose">
                工作坊結束後，合作學協會需依指定格式提供完訓學員名單，作為後續證書核發與學員下載依據。
              </p>
              <p class="note">註：完訓名單提供請依通知信辦理。</p>
            </div>
          </div>
        </div>

        <!-- 右側：個人證書下載 -->
        <div class="card card-right">
          <div class="card-content">
            <h2 class="card-title">個人證書下載</h2>
            <div class="card-info">
              <p class="target-group">
                適用對象：<span class="highlight-teal">完訓學員</span>
              </p>
              <p class="purpose">
                學員完成工作坊後，待合作學協會提供完訓名單並經管理單位確認，即可登入網站下載個人證書。
              </p>
            </div>
          </div>
          <div class="card-action">
            <router-link to="/Certificate" class="action-btn btn-teal">
              前往下載證書
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="btn-icon"
              >
                <path
                  d="M5 12H19M19 12L12 5M19 12L12 19"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- 認證申請流程區塊 -->
    <div class="process-section-wrapper">
      <div class="process-section">
        <h2 class="process-title">認證申請流程</h2>
        <p class="process-desc">
          完訓名單由合作學協會提供，經管理單位確認後，符合資格的學員即可登入網站下載個人完訓證書。
        </p>

        <div class="process-steps">
          <div class="step">
            <div class="step-header">工作坊辦理</div>
            <div class="step-body">
              合作學協會辦理工作坊，並確認學員完成課程培訓。
            </div>
          </div>
          <div class="step">
            <div class="step-header">名單提供</div>
            <div class="step-body">
              合作學協會依指定格式提供完訓學員名單，作為證書核發依據。
            </div>
          </div>
          <div class="step">
            <div class="step-header">資格確認</div>
            <div class="step-body">
              管理單位確認完訓名單與學員資料，完成完訓資格建置。
            </div>
          </div>
          <div class="step">
            <div class="step-header">資料補填</div>
            <div class="step-body">
              學員登入網站，補齊證書下載所需會員資料。
            </div>
          </div>
          <div class="step">
            <div class="step-header">證書下載</div>
            <div class="step-body">符合資格的學員即可下載個人完訓證書。</div>
          </div>
        </div>
      </div>
    </div>
    <FooterSection />
  </div>
</template>

<script setup>
import { useLayoutStore } from "@/stores/layout";
import { useI18n } from "vue-i18n";
import Navbar from "@/components/Navbar.vue";
import PageBanner from "@/components/PageBanner.vue";
import FooterSection from "@/components/FooterSection.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";

const layoutStore = useLayoutStore();
const { t } = useI18n();

const breadcrumbItems = [
  { label: t("navbar.home"), path: "/" },
  { label: t("navbar.training") },
  { label: t("navbar.certificationApply") },
];
</script>

<style scoped lang="scss">
.certification-apply-page {
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  padding: 80px 20px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
}

.cards-container {
  display: flex;
  gap: 24px;
  width: 100%;
}

.card {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  /* CSS settings according to requirement */
  padding-top: 80px;
  padding-right: 64px;
  padding-bottom: 80px;
  padding-left: 64px;
  border-radius: 20px;
  opacity: 1;
}

.card-left {
  background: linear-gradient(
    0deg,
    rgba(0, 139, 205, 0.1),
    rgba(0, 139, 205, 0.1)
  );
}

.card-right {
  gap: 40px;
  background: linear-gradient(
    0deg,
    rgba(0, 131, 129, 0.1),
    rgba(0, 131, 129, 0.1)
  );
}

.card-title {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 32px;
  line-height: 1.4;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.card-info p {
  font-size: 20px;
  color: #333;
  margin: 0;
  line-height: 1.5;
}

.card-info .note {
  font-size: 16px;
  color: #666;
  margin-top: 16px;
}

/* 認證申請流程樣式 */
.process-section-wrapper {
  background-color: #f9f9f9;
  width: 100%;
  padding: 60px 20px;
}

.process-section {
  max-width: 1200px;
  margin: 0 auto;
}

.process-title {
  color: #0079bb;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 16px;
}

.process-desc {
  color: #333;
  font-size: 16px;
  margin-bottom: 40px;
  line-height: 1.5;
}

.process-steps {
  display: flex;
  justify-content: space-between;
  position: relative;
  gap: 16px;
}

/* 連接線 */
.process-steps::before {
  content: "";
  position: absolute;
  top: 28px; /* 56px 的一半 */
  left: 110px; /* step-header 的一半 */
  right: 110px;
  height: 2px;
  background-color: #0079bb;
  z-index: 0;
}

.step {
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 220px;
  position: relative;
  z-index: 1;
}

.step-header {
  width: 100%;
  height: 56px;
  background-color: #0079bb;
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-body {
  background-color: #ffffff;
  padding: 16px;
  color: #333;
  font-size: 15px;
  line-height: 1.5;
  flex: 1;
  box-sizing: border-box;
}

.highlight-blue {
  color: #0079bb;
  font-weight: 600;
}

.highlight-teal {
  color: #008381;
  font-weight: 600;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 12px 32px;
  gap: 8px;
  border-radius: 50px;
  color: #ffffff;
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  text-decoration: none;
}

.btn-icon {
  margin-left: 4px;
}

.btn-blue {
  background-color: #0079bb;
}

.btn-blue:hover {
  background-color: #005a8c;
}

.btn-teal {
  background-color: #008381;
}

.btn-teal:hover {
  background-color: #006b69;
}

@media (max-width: 960px) {
  .cards-container {
    flex-direction: column;
  }

  .card {
    padding: 40px 32px;
  }

  .card-title {
    font-size: 24px;
    margin-bottom: 24px;
  }

  .card-info p {
    font-size: 16px;
  }

  .card-action {
    margin-top: 32px;
  }

  /* 手機版認證申請流程 */
  .process-steps {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
  }

  .process-steps::before {
    display: none;
  }

  .step {
    max-width: 100%;
    width: 100%;
  }
}
</style>